import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Wave 2.28 BE-2 (TASK-094c) — wire schemas for the admin mood-compat matrix
 * editor (admin-prd §12.2 settings, decisions §35, admin-arch §3 lines
 * 228-233).
 *
 * Storage shape: `internal.mood_compat` is a symmetric graph keyed by mood
 * tag UUIDs (`mood_a_id`, `mood_b_id` → `content.tags.id` WHERE
 * `category.code = 'mood'`). Backend stores BOTH directions `(A,B,w)` and
 * `(B,A,w)` on insert so the rotation read path is a single keyed lookup
 * (see `apps/api/src/rotation/mood-compat.service.ts`).
 *
 * Wire shape: the admin editor sends ONE entry per unordered pair (the FE
 * renders the upper triangle of an N×N matrix). Backend mirrors each entry
 * into both directions during the bulk-replace transaction. Diagonal entries
 * `mood_a_id === mood_b_id` are allowed — they encode the own-weight of a
 * mood (decisions §35 calls this 1.5 by default).
 *
 * Weight range: numeric(3,2) in `[0.00 .. 2.00]` per the existing DB check
 * constraint `mood_compat_weight_check`. Neutral default is 1.0 (the
 * rotation service falls back to 1.0 when a pair is missing). 0 fully
 * blocks the candidate; values > 1 boost it.
 *
 * The mood list is NOT a hardcoded 9-mood enum: mood-category tags are
 * curator-managed (migration 0008 seeds the `mood` CATEGORY only, not
 * individual mood tags). The GET endpoint returns the active mood-tag set
 * so the FE renders an N×N grid of whatever count is currently active.
 */
/** Weight ∈ [0, 2]. Two decimal places match `numeric(3,2)` column. */
export const moodCompatWeightSchema = z
    .number()
    .min(0)
    .max(2)
    .refine((n) => Number.isFinite(n), 'weight must be a finite number');
/**
 * Mood-tag descriptor returned in the GET response. Drives the matrix axis
 * labels on the FE.
 */
export const moodTagSchema = z.object({
    id: uuidSchema,
    /** Human-readable tag name, e.g. "chill", "dreamy" (content.tags.name). */
    name: z.string().min(1).max(255),
});
/**
 * A single edge in the symmetric graph. `mood_a_id` MAY equal `mood_b_id`
 * (own-weight). Within a single PUT payload each unordered pair MUST appear
 * at most once — the backend enforces this and rejects duplicates with a
 * `VALIDATION_ERROR` issue.
 */
export const moodCompatPairSchema = z
    .object({
    mood_a_id: uuidSchema,
    mood_b_id: uuidSchema,
    weight: moodCompatWeightSchema,
})
    .strict();
/**
 * GET response — full editable state of the matrix.
 *
 * `moods` — active mood-category tags (alphabetical by name for stable
 * rendering; no `display_order` column exists on `content.tags`, so name
 * is the canonical sort key).
 * `pairs` — every row currently in `internal.mood_compat` collapsed to the
 * canonical orientation `mood_a_id <= mood_b_id` so the FE never receives
 * the same edge twice.
 * `updated_at` — most recent edit timestamp across the matrix (max of
 * audit-log entries for `mood_compat.updated`, or `null` if never edited).
 */
export const moodCompatMatrixSchema = z.object({
    moods: z.array(moodTagSchema),
    pairs: z.array(moodCompatPairSchema),
    updated_at: isoDateTimeSchema.nullable(),
});
/**
 * PUT body — bulk replace. Every supplied pair MUST reference an active
 * mood-category tag (the service joins on `content.tags` + `tag_categories`
 * and rejects with `MOOD_COMPAT_INVALID_TAG` if a foreign or non-mood tag
 * leaks in).
 *
 * Sending an empty `pairs` array CLEARS the matrix (rotation falls back to
 * neutral 1.0 for every lookup — see `MoodCompatService.getScore`). The
 * editor MUST send the full intended state — the operation is a replace,
 * not a merge.
 */
export const moodCompatUpdateSchema = z
    .object({
    pairs: z
        .array(moodCompatPairSchema)
        .max(10_000, 'too many pairs in a single request'),
})
    .strict();
//# sourceMappingURL=mood-compat.js.map